/**
 * The six step components that make up the booking wizard.
 *
 * Each step is a presentational component: it receives data + selection
 * callbacks from the parent `BookingPage`, renders its UI, and never owns
 * cross-step state. This keeps the data-flow obvious and unit-testable.
 *
 * The CSS in this project uses an inverted dark-mode convention, so the
 * parent must always pass an `isVisuallyDark` flag (i.e. `!isDarkMode`)
 * via `palette` from `getBookingPalette()`.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  armenianToLatin,
  fetchRatingsByWorker,
  pickAddress,
  pickTranslation,
  resolveStorageUrl,
} from "../../helper/bookingApi";
import { resolveImageUrl } from "./BookingTheme";
import {
  ArrowRightIcon,
  BOOKING_FONT_FAMILY,
  EmptyState,
  RetryButton,
  SearchInput,
  SelectableCard,
  SkeletonRow,
  StepCard,
  StepFooter,
  StepHeader,
  STEP_PAD,
} from "./BookingPrimitives";

/* =====================================================================
 * Step 1 — Industry (organization-type)
 * ===================================================================== */

export function IndustryStep({ palette, lang, industries, loading, error, selectedId, onSelect, onRetry }) {
  const { t } = useTranslation();
  const isDarkBookingSurface = palette.surface === "#191C1F";
  const industryCardPalette = isDarkBookingSurface
    ? {
      ...palette,
      surfaceAlt: "#202428",
      surfaceHover: "#2a2f34",
    }
    : palette;

  return (
    <StepCard palette={palette}>
      <StepHeader
        palette={palette}
        title={t("booking.industry.title")}
        subtitle={t("booking.industry.subtitle")}
      />
      <div
        style={{
          padding: `${STEP_PAD.contentTop} ${STEP_PAD.x} ${STEP_PAD.contentBottom} ${STEP_PAD.x}`,
        }}
      >
        {loading ? (
          <SkeletonRow palette={palette} count={5} />
        ) : error ? (
          <EmptyState
            palette={palette}
            icon="las la-exclamation-circle"
            title={t("booking.errors.loadIndustries")}
            action={<RetryButton palette={palette} onClick={onRetry} />}
          />
        ) : industries.length === 0 ? (
          <EmptyState
            palette={palette}
            icon="las la-folder-open"
            title={t("booking.common.noResults")}
          />
        ) : (
          <div
            className="booking-industry-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 24,
            }}
          >
            {industries.map((industry) => {
              const name = pickTranslation(industry, lang);
              const img = resolveImageUrl(industry.image_path);
              return (
                <SelectableCard
                  key={industry.id}
                  palette={industryCardPalette}
                  selected={selectedId === industry.id}
                  onClick={() => onSelect(industry)}
                  iconImg={img || null}
                  icon={img ? null : "las la-tag"}
                  iconSize={82}
                  height="auto"
                  padding="20px 18px"
                  unselectedBorderColor={isDarkBookingSurface ? "#32383E" : "#DFE1F3"}
                  selectedBorderColor={isDarkBookingSurface ? "#32383E" : "#DFE1F3"}
                  title={name}
                />
              );
            })}
          </div>
        )}
      </div>
      <style>{`
        @media (max-width: 991px) {
          .booking-industry-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }

        @media (max-width: 575px) {
          .booking-industry-grid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
        }
      `}</style>
    </StepCard>
  );
}

/* =====================================================================
 * Step 2 — Organization (filtered by selected industry)
 *
 * Layout: responsive card grid (1 col mobile / 2 col tablet / 3 col xl).
 * Each card surfaces the org's identity AND a small preview of the first
 * branches when the API returns them, so users can shortcut-click a
 * specific branch and skip Step 3 entirely. This matches the density of
 * the designer's mockup while keeping click targets explicit.
 * ===================================================================== */

const BRANCH_PREVIEW_COUNT = 3;

function makeBookingToken(id, label) {
  if (id == null) return "";
  const safeLabel = armenianToLatin(String(label || id))
    .trim()
    .replace(/[\\/]+/g, "-")
    .replace(/\s+/g, "-");
  return `${id},${encodeURIComponent(safeLabel || id)}`;
}

function makeOrganizationPublicHref(org, lang, name) {
  const base = lang === "en" ? "/en/online-booking/org" : "/online-booking/org";
  return `${base}/${makeBookingToken(org.id, name)}`;
}

function getOrgBranches(org) {
  if (!org) return [];
  if (Array.isArray(org.branches)) return org.branches;
  if (Array.isArray(org.branch)) return org.branch;
  return [];
}

function getOrgBranchCount(org) {
  if (org?.branches_count != null) return org.branches_count;
  if (org?.branch_count != null) return org.branch_count;
  return getOrgBranches(org).length;
}

function OrgBranchRow({ palette, branch, lang, onClick }) {
  const name = pickTranslation(branch, lang);
  const address = pickAddress(branch);
  const isLightOrgCard = palette.surface === "hsl(0 0% 100%)";
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      className="booking-org-card__branch"
    >
      <i
        className="las la-map-marker-alt"
        style={{ color: '#00D3A1' }}
        aria-hidden="true"
      />
      <span className="booking-org-card__branch-copy">
        <span className="booking-org-card__branch-name">{name}</span>
        {address ? (
          <span className="booking-org-card__branch-address">
            {address}
          </span>
        ) : null}
      </span>
      <span className="booking-org-card__branch-arrow">
        <ArrowRightIcon color={isLightOrgCard ? "#202532" : palette.textSubtle} size={16} />
      </span>
    </button>
  );
}

// Kept temporarily as a rollback reference while the Figma card is validated.
// eslint-disable-next-line no-unused-vars
function OrgCard({
  palette,
  org,
  lang,
  selected,
  onSelectBranch,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const name = pickTranslation(org, lang);
  const img = resolveImageUrl(org.image_path);
  const publicHref = makeOrganizationPublicHref(org, lang, name);
  const branches = getOrgBranches(org);
  const branchCount = getOrgBranchCount(org);
  const preview = branches.slice(0, BRANCH_PREVIEW_COUNT);

  const borderColor = selected ? palette.accentBorder : "transparent";
  const showBranchPreview = preview.length > 0;
  const openPublicPage = () =>
    navigate(publicHref, {
      state: { organization: org },
    });

  return (
    <div
      className={`booking-org-card${selected ? " is-selected" : ""}`}
      role="link"
      tabIndex={0}
      onClick={openPublicPage}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openPublicPage();
        }
      }}
      aria-label={`${t("booking.organization.viewPublicPage")}: ${name}`}
      style={{
        borderColor,
      }}
    >
      {/* Top click target: select the whole org → Step 3 */}
      <div
        style={{
          minHeight: 92,
          background: img
            ? `linear-gradient(90deg, rgba(15,23,42,.72), rgba(15,23,42,.16)), url(${img})`
            : `linear-gradient(135deg, ${palette.accentSoft}, ${palette.surfaceHover})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderBottom: palette.border,
          position: "relative",
        }}
      >
        <span
          style={{
            position: "absolute",
            left: 14,
            bottom: -26,
            width: 58,
            height: 58,
            borderRadius: 16,
            background: palette.surface,
            border: palette.border,
            color: palette.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.65rem",
            boxShadow: palette.shadowSoft,
            overflow: "hidden",
          }}
          aria-hidden="true"
        >
          <i className="las la-store" />
        </span>
        {branchCount > 0 ? (
          <span
            style={{
              position: "absolute",
              right: 12,
              bottom: 12,
              background: "rgba(15, 23, 42, 0.72)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,.22)",
              borderRadius: 999,
              padding: "5px 10px",
              fontSize: "0.78rem",
              fontWeight: 700,
              backdropFilter: "blur(8px)",
            }}
          >
            <i className="las la-map-marker-alt me-1" aria-hidden="true" />
            {t("booking.organization.branchCount", { count: branchCount })}
          </span>
        ) : null}
        {/* <Link
          to={publicHref}
          onClick={(event) => event.stopPropagation()}
          title={t("booking.organization.viewPublicPage")}
          aria-label={t("booking.organization.viewPublicPage")}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.94)",
            color: "hsl(166 100% 28%)",
            border: "1px solid rgba(255,255,255,0.85)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            textDecoration: "none",
            boxShadow: "0 10px 22px rgba(0,0,0,0.18)",
          }}
        >
          <i className="las la-eye" aria-hidden="true" />
        </Link> */}
      </div>
      <div
        className="text-start w-100"
        style={{
          background: "transparent",
          border: "none",
          padding: "34px clamp(12px, 3vw, 16px) 12px clamp(12px, 3vw, 16px)",
          cursor: "pointer",
          color: palette.text,
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
        }}
      >
        <div className="d-flex align-items-start gap-3">
          {img ? (
            <span
              style={{
                width: "clamp(40px, 9vw, 48px)",
                height: "clamp(40px, 9vw, 48px)",
                borderRadius: 12,
                background: palette.surface,
                border: palette.border,
                display: "none",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <img
                src={img}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </span>
          ) : (
            <span
              style={{
                width: "clamp(40px, 9vw, 48px)",
                height: "clamp(40px, 9vw, 48px)",
                borderRadius: 12,
                background: palette.surface,
                border: palette.border,
                color: palette.accent,
                display: "none",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.4rem",
                flexShrink: 0,
              }}
            >
              <i className="las la-building" aria-hidden="true" />
            </span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              className="fw-700"
              style={{
                fontSize: "1.05rem",
                color: palette.text,
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              {name}
            </div>
            {branchCount > 0 ? (
              <div
                style={{
                  fontSize: "0.78rem",
                  color: palette.textMuted,
                  marginTop: 4,
                }}
              >
                <i
                  className="las la-map-marker-alt me-1"
                  aria-hidden="true"
                />
                {t("booking.organization.branchCount", { count: branchCount })}
              </div>
            ) : null}
            <div className="d-flex flex-wrap gap-2" style={{ marginTop: 8 }}>
              <span
                style={{
                  background: palette.accentSoft,
                  color: palette.accent,
                  borderRadius: 999,
                  padding: "4px 9px",
                  fontSize: "0.74rem",
                  fontWeight: 700,
                }}
              >
                {t("booking.organization.publicBookingBadge")}
              </span>
              {showBranchPreview ? (
                <span
                  style={{
                    color: palette.textMuted,
                    fontSize: "0.8rem",
                    padding: "4px 0",
                  }}
                >
                  {t("booking.organization.pickBranchHint")}
                </span>
              ) : null}
            </div>
          </div>
          {selected ? (
            <span
              aria-hidden="true"
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: palette.accent,
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.85rem",
                flexShrink: 0,
              }}
            >
              <i className="las la-check" />
            </span>
          ) : null}
        </div>
      </div>

      {/* Branch preview list (clickable shortcuts) */}
      {showBranchPreview ? (
        <div
          style={{
            padding: "0 10px 10px 10px",
            borderTop: palette.border,
            paddingTop: 10,
            display: "grid",
            gap: 4,
          }}
        >
          {preview.map((branch) => (
            <OrgBranchRow
              key={branch.id}
              palette={palette}
              branch={branch}
              lang={lang}
              onClick={() => onSelectBranch(org, branch)}
            />
          ))}
        </div>
      ) : null}

      {/* Footer CTA — commits the selection AND advances to the next step.
          (The card header is "soft select" so users can browse without
          committing; this button is the explicit "I'm done choosing" path.) */}
    </div>
  );
}

function OrgCardRedesigned({
  palette,
  org,
  lang,
  selected,
  onSelectBranch,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const name = pickTranslation(org, lang);
  const img = resolveImageUrl(org.image_path);
  const publicHref = makeOrganizationPublicHref(org, lang, name);
  const branches = getOrgBranches(org);
  const branchCount = getOrgBranchCount(org);
  const preview = branches.slice(0, BRANCH_PREVIEW_COUNT);
  const showBranchPreview = preview.length > 0;
  const borderColor = selected ? palette.accentBorder : "transparent";
  const isLightOrgCard = palette.surface === "hsl(0 0% 100%)";
  const orgCardColors = isLightOrgCard
    ? {
      card: "#f4f5fc",
      header: "#dfe1f4",
      border: "#d9ddeb",
      logoBg: "#ffffff",
      text: "#202532",
      muted: "#6f7788",
      hover: "#eceef8",
      countBg: "#DFE1F3",
      countBorder: "#cbd5df",
    }
    : {
      card: "#202428",
      header:
        "#111315",
      border: "rgba(255, 255, 255, 0.12)",
      logoBg: "#191C1F",
      text: "#f5f5f5",
      muted: "#bfbfbf",
      hover: "rgba(255, 255, 255, 0.06)",
      countBg: "#11131566",
      countBorder: "rgba(255, 255, 255, 0.22)",
    };
  const openPublicPage = () =>
    navigate(publicHref, {
      state: { organization: org },
    });
  const countLabel = t("booking.organization.branchCount", {
    count: branchCount,
  });

  return (
    <div
      className={`booking-org-card${selected ? " is-selected" : ""}`}
      role="link"
      tabIndex={0}
      onClick={openPublicPage}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openPublicPage();
        }
      }}
      aria-label={`${t("booking.organization.viewPublicPage")}: ${name}`}
      style={{ borderColor }}
    >
      <div className="booking-org-card__header">
        <span className="booking-org-card__logo" aria-hidden="true">
          {img ? (
            <img
              src={img}
              alt=""
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <i className="las la-building" aria-hidden="true" />
          )}
        </span>
        <h4 className="booking-org-card__title">{name}</h4>
        {selected ? (
          <span className="booking-org-card__selected" aria-hidden="true">
            <i className="las la-check" />
          </span>
        ) : null}
      </div>

      <p className="booking-org-card__hint">
        {t("booking.organization.pickBranchHint")}
      </p>

      {showBranchPreview ? (
        <div className="booking-org-card__branches">
          {preview.map((branch) => (
            <OrgBranchRow
              key={branch.id}
              palette={palette}
              branch={branch}
              lang={lang}
              onClick={() => onSelectBranch(org, branch)}
            />
          ))}
        </div>
      ) : null}

      {branchCount > BRANCH_PREVIEW_COUNT ? (
        <button
          type="button"
          className="booking-org-card__count-pill"
      
          onClick={(event) => {
            event.stopPropagation();
            openPublicPage();
          }}
        >
          <i className="las la-map-marker-alt" aria-hidden="true" style={{ color: '#00D3A1' }} />
          +{countLabel}
        </button>
      ) : null}

      <style>{`
        .booking-org-card {
          width: 100%;
          max-width: 397px;
          min-height: 399px;
          background: ${orgCardColors.card};
          border: 2px solid transparent;
          border-radius: 18px;
          color: ${orgCardColors.text};
          cursor: pointer;
          display: flex;
          flex-direction: column;
          font-family: ${BOOKING_FONT_FAMILY};
          overflow: hidden;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
        }

        .booking-org-card:hover,
        .booking-org-card:focus-visible {
          box-shadow: ${palette.shadow};
          outline: none;
          transform: translateY(-1px);
        }

        .booking-org-card.is-selected {
          box-shadow: 0 0 0 1px ${palette.accentBorder}, ${palette.shadow};
        }

        .booking-org-card__header {
          min-height: 98px;
          padding: 16px;
          border-bottom: 1px solid ${orgCardColors.border};
          background: ${orgCardColors.header};
          display: flex;
          align-items: flex-start;
          gap: 10px;
          position: relative;
        }

        .booking-org-card__logo {
          width: 58px;
          height: 58px;
          border: 1px solid ${orgCardColors.border};
          border-radius: 16px;
          background: ${orgCardColors.logoBg};
          box-shadow: ${palette.shadowSoft};
          color: ${palette.accent};
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 58px;
          font-size: 26px;
          overflow: hidden;
        }

        .booking-org-card__logo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .booking-org-card__title {
          color: ${orgCardColors.text};
          font-size: 16.8px;
          font-weight: 700;
          line-height: 21.84px;
          margin: 0;
          min-width: 0;
          padding-top: 1px;
          word-break: break-word;
        }

        .booking-org-card__selected {
          width: 24px;
          height: 24px;
          border-radius: 999px;
          background: ${palette.accent};
          color: ${palette.stepActiveText};
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 24px;
          font-size: 15px;
          margin-left: auto;
        }

        .booking-org-card__hint {
          min-height: 67px;
          margin: 0;
          padding: 18px 16px 10px;
          color: ${orgCardColors.muted};
          font-size: 12.8px;
          font-weight: 400;
          line-height: 19.2px;
          word-break: break-word;
        }

        .booking-org-card__branches {
          border-top: 1px solid ${orgCardColors.border};
          padding: 11px 10px 10px;
          display: grid;
          gap: 0;
        }

        .booking-org-card__branch {
          width: 100%;
          min-height: 52px;
          padding: 8px 10px;
          border: 0;
          border-radius: 8px;
          background: transparent;
          color: ${orgCardColors.text};
          cursor: pointer;
          display: flex;
          align-items: flex-start;
          gap: 8px;
          text-align: left;
          transition: background 0.15s ease;
        }

        .booking-org-card__branch:hover,
        .booking-org-card__branch:focus-visible {
          background: ${orgCardColors.hover};
          outline: none;
        }

        .booking-org-card__branch > i {
          color: ${orgCardColors.text};
          flex: 0 0 16px;
          font-size: 16px;
          line-height: 16px;
          margin-top: 1px;
        }

        .booking-org-card__branch-copy {
          flex: 1 1 auto;
          min-width: 0;
        }

        .booking-org-card__branch-name,
        .booking-org-card__branch-address {
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .booking-org-card__branch-name {
          color: ${orgCardColors.text};
          font-size: 13.6px;
          font-weight: 600;
          line-height: 17px;
        }

        .booking-org-card__branch-address {
          color: ${orgCardColors.muted};
          font-size: 12px;
          font-weight: 400;
          line-height: 18px;
        }

        .booking-org-card__branch-arrow {
          flex: 0 0 16px;
          margin-top: 8px;
        }

        .booking-org-card__count-pill {
          align-self: center;
          margin: auto 0 20px;
          min-width: 132px;
          max-width: calc(100% - 32px);
          padding: 6px 14px;
          border: 1px solid ${orgCardColors.countBorder};
          border-radius: 999px;
          background: ${orgCardColors.countBg};
          backdrop-filter: blur(4px);
          color: ${orgCardColors.text};
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          font-size: 12.5px;
          font-weight: 700;
          line-height: 18.72px;
          white-space: nowrap;
        }

        .booking-org-card__count-pill:hover,
        .booking-org-card__count-pill:focus-visible {
          border-color: ${palette.accentBorder};
          outline: none;
        }

        .booking-org-card__count-pill i {
          font-size: 16px;
          line-height: 16px;
        }

        @media (max-width: 575px) {
          .booking-org-card {
            max-width: none;
            min-height: 360px;
          }

          .booking-org-card__header {
            padding: 14px;
          }

          .booking-org-card__title {
            font-size: 15.5px;
            line-height: 20px;
          }

          .booking-org-card__hint {
            min-height: auto;
          }
        }
      `}</style>
    </div>
  );
}

export function OrganizationStep({
  palette,
  lang,
  industry,
  organizations,
  loading,
  error,
  selectedId,
  onSelect,
  onSelectBranchShortcut,
  onBack,
  onRetry,
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return organizations;
    const q = search.toLowerCase().trim();
    return organizations.filter((org) => {
      const name = pickTranslation(org, lang).toLowerCase();
      const address = pickAddress(org).toLowerCase();
      return name.includes(q) || address.includes(q);
    });
  }, [organizations, search, lang]);

  return (
    <StepCard palette={palette}>
      <div
        className="booking-organization-topbar"
        style={{
          borderBottom: palette.border,
          padding: "16px 24px 21px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          className="booking-organization-back"
          style={{
            background: "transparent",
            border: palette.border,
            borderRadius: 40,
            color: palette.textMuted,
            padding: "9px 19px",
            minHeight: 42,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            fontSize: 16,
            lineHeight: "24px",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <i className="las la-arrow-left" aria-hidden="true" />
          {t("booking.common.back")}
        </button>
        {industry ? (
          <span
            className="booking-organization-industry-pill"
            style={{
              color: palette.textMuted,
              fontSize: 14,
              lineHeight: "20px",
              whiteSpace: "nowrap",
            }}
          >
            / {pickTranslation(industry, lang)}
          </span>
        ) : null}
      </div>
      <StepHeader
        palette={palette}
        title={t("booking.organization.fastTitle")}
        subtitle={t("booking.organization.fastSubtitle")}
        subtitleStyle={{ fontSize: 14, whiteSpace: "pre-line" }}
        copyStyle={{ flex: "1 1 320px" }}
        accessoryStyle={{ flex: "1 1 481px", maxWidth: 481, width: "100%" }}
        accessory={(
          <SearchInput
            palette={palette}
            value={search}
            onChange={setSearch}
            placeholder={t("booking.common.search")}
          />
        )}
      />
      <div style={{ padding: `4px ${STEP_PAD.x} 10px ${STEP_PAD.x}` }}>
        {/* <div
          className="booking-organization-hint"
          style={{
            background: palette.accentSoft,
            border: `1px solid ${palette.accentBorder}`,
            borderRadius: 14,
            padding: "17px 15px 13px",
            color: palette.text,
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            style={{
              color: palette.accent,
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <path
              d="M12 11v5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <path
              d="M12 8h.01"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              className="fw-700 d-block"
              style={{
                color: palette.text,
                fontSize: "15.2px",
                lineHeight: "22.8px",
              }}
            >
              {t("booking.organization.fastTitle")}
            </span>
            <span
              style={{
                color: palette.textMuted,
                fontSize: "13.4px",
                lineHeight: "18.82px",
              }}
            >
              {t("booking.organization.fastSubtitle")}
            </span>
          </span>
        </div> */}
        {/* {showBeautyDemo ? (
          <div
            style={{
              marginTop: 10,
              background: palette.surfaceAlt,
              border: palette.border,
              borderRadius: 14,
              padding: "12px 14px",
              color: palette.text,
              display: "flex",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: 12,
                background: palette.accentSoft,
                color: palette.accent,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: "1.2rem",
              }}
              aria-hidden="true"
            >
              <i className="las la-magic" />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="fw-700 d-block" style={{ fontSize: "0.95rem" }}>
                {t("booking.organization.beautyDemoTitle")}
              </span>
            </span>
            <Link
              to={beautyDemoHref}
              className="pill fw-600"
              style={{
                background: palette.accent,
                color: "#fff",
                border: "none",
                padding: "9px 14px",
                textDecoration: "none",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                maxWidth: "100%",
              }}
            >
              {t("booking.organization.beautyDemoCta")}
              <ArrowRightIcon color="#fff" size={14} />
            </Link>
          </div>
        ) : null} */}
      </div>
      <div
        style={{
          padding: `12px ${STEP_PAD.x} ${STEP_PAD.contentBottom} ${STEP_PAD.x}`,
        }}
      >
        {loading ? (
          <div className="row g-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="col-12 col-sm-6 col-xl-4">
                <div
                  style={{
                    height: 180,
                    borderRadius: 16,
                    background: palette.surfaceAlt,
                    border: palette.border,
                  }}
                />
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            palette={palette}
            icon="las la-exclamation-circle"
            title={t("booking.errors.loadOrganizations")}
            action={<RetryButton palette={palette} onClick={onRetry} />}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            palette={palette}
            icon="las la-search-minus"
            title={t("booking.common.noResults")}
          />
        ) : (
          <div className="row g-3">
            {filtered.map((org) => (
              <div key={org.id} className="col-12 col-sm-6 col-xl-4">
                <OrgCardRedesigned
                  palette={palette}
                  org={org}
                  lang={lang}
                  selected={selectedId === org.id}
                  onSelectBranch={(o, b) => {
                    if (typeof onSelectBranchShortcut === "function") {
                      onSelectBranchShortcut(o, b);
                    } else {
                      onSelect(o);
                    }
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`
        .booking-organization-back:hover,
        .booking-organization-back:focus-visible {
          border-color: ${palette.accentBorder} !important;
          color: ${palette.text} !important;
          outline: none;
        }

        @media (max-width: 575px) {
          .booking-organization-topbar {
            padding: 14px 16px !important;
          }

          .booking-organization-back {
            min-height: 42px !important;
            padding: 9px 19px !important;
            font-size: 16px !important;
            white-space: nowrap !important;
            flex-shrink: 0;
          }

          .booking-organization-industry-pill {
            max-width: 55%;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      `}</style>
    </StepCard>
  );
}

/* =====================================================================
 * Step 3 — Branch
 * ===================================================================== */

export function BranchStep({
  palette,
  lang,
  organization,
  branches,
  loading,
  error,
  selectedId,
  onSelect,
  onBack,
  onNext,
  onRetry,
}) {
  const { t } = useTranslation();
  const orgName = organization ? pickTranslation(organization, lang) : "";

  return (
    <StepCard palette={palette}>
      <StepHeader
        palette={palette}
        title={t("booking.branch.title")}
        subtitle={t("booking.branch.subtitle", { org: orgName })}
      />
      <div
        style={{
          padding: `${STEP_PAD.contentTop} ${STEP_PAD.x} ${STEP_PAD.contentBottom} ${STEP_PAD.x}`,
        }}
      >
        {loading ? (
          <SkeletonRow palette={palette} count={3} />
        ) : error ? (
          <EmptyState
            palette={palette}
            icon="las la-exclamation-circle"
            title={t("booking.errors.loadBranches")}
            action={<RetryButton palette={palette} onClick={onRetry} />}
          />
        ) : branches.length === 0 ? (
          <EmptyState
            palette={palette}
            icon="las la-map-marker"
            title={t("booking.common.noResults")}
          />
        ) : (
          <div className="d-grid gap-2">
            {branches.map((branch) => {
              const name = pickTranslation(branch, lang);
              const address =
                pickAddress(branch) || t("booking.branch.addressFallback");
              return (
                <SelectableCard
                  key={branch.id}
                  palette={palette}
                  selected={selectedId === branch.id}
                  onClick={() => onSelect(branch)}
                  icon="las la-map-marker-alt"
                  title={name}
                  subtitle={address}
                  trailing={<ArrowRightIcon color={palette.textSubtle} size={18} />}
                />
              );
            })}
          </div>
        )}
      </div>
      <StepFooter palette={palette}>
        <button
          type="button"
          onClick={onBack}
          className="pill fw-500"
          style={{
            background: "transparent",
            border: palette.border,
            color: palette.textMuted,
            padding: "8px 18px",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <i className="las la-arrow-left me-2" aria-hidden="true" />
          {t("booking.common.back")}
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!selectedId}
          className="pill fw-500"
          style={{
            background: palette.accent,
            color: "#fff",
            border: "none",
            padding: "10px 24px",
            opacity: selectedId ? 1 : 0.5,
          }}
        >
          {t("booking.common.next")}
          <i className="las la-arrow-right ms-2" aria-hidden="true" />
        </button>
      </StepFooter>
    </StepCard>
  );
}

function EntryOption({ palette, icon, title, subtitle, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="booking-entry-option text-start w-100"
    >
      <span className="booking-entry-option__icon" aria-hidden="true">
        <i className={icon} />
      </span>
      <span className="booking-entry-option__copy">
        <span className="booking-entry-option__title">{title}</span>
        <span className="booking-entry-option__subtitle">{subtitle}</span>
      </span>
      <ArrowRightIcon color={palette.textSubtle} size={18} />
    </button>
  );
}

export function BookingEntryChoiceStep({
  palette,
  industry,
  organization,
  branch,
  lang,
  onChooseSpecialist,
  onChooseService,
  onBack,
}) {
  const { t } = useTranslation();
  const organizationName = organization ? pickTranslation(organization, lang) : "";
  const branchName = branch ? pickTranslation(branch, lang) : "";
  const branchAddress = branch ? pickAddress(branch, lang) : "";
  const displayName = branchName || organizationName;
  const displayMeta = branchAddress;
  const orgImage = resolveImageUrl(organization?.image_path);
  const industryName = getLinkedIndustryName({ industry, organization, branch, lang });

  return (
    <StepCard
      palette={palette}
      style={{
        borderRadius: 14,
        boxShadow: palette.shadowSoft,
        overflow: "hidden",
      }}
    >
      <div className="booking-entry-topbar">
        <button type="button" onClick={onBack} className="booking-entry-back">
          <i className="las la-arrow-left" aria-hidden="true" />
          {t("booking.common.back")}
        </button>
        <span className="booking-entry-breadcrumb">
          {industryName ? `${industryName} / ` : ""}
          {organizationName}
        </span>
      </div>
      <div
        className="booking-entry-body"
        style={{
          display: "grid",
          gap: 16,
        }}
      >
        <div className="booking-entry-branch">
          <span className="booking-entry-logo" aria-hidden="true">
            {orgImage ? <img src={orgImage} alt="" /> : <i className="las la-store" />}
          </span>
          <div className="booking-entry-branch__copy">
            {displayName ? (
              <h2>{displayName}</h2>
            ) : null}
            {displayMeta ? (
              <p>{displayMeta}</p>
            ) : null}
          </div>
        </div>

        <div className="booking-entry-intro">
          <h3>{t("booking.entryChoice.title")}</h3>
          <p>{t("booking.entryChoice.subtitle", { branch: displayName })}</p>
        </div>

        <div className="booking-entry-options">
          <EntryOption
            palette={palette}
            icon="las la-user-friends"
            title={t("booking.entryChoice.specialistTitle")}
            subtitle={t("booking.entryChoice.specialistSubtitle")}
            onClick={onChooseSpecialist}
          />
          <EntryOption
            palette={palette}
            icon="las la-list-ul"
            title={t("booking.entryChoice.serviceTitle")}
            subtitle={t("booking.entryChoice.serviceSubtitle")}
            onClick={onChooseService}
          />
        </div>
      </div>
      <style>{`
        .booking-entry-topbar {
          min-height: 60px;
          padding: 14px 24px;
          border-bottom: ${palette.border};
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .booking-entry-back {
          min-height: 34px;
          padding: 7px 14px;
          border: ${palette.border};
          border-radius: 8px;
          background: transparent;
          color: ${palette.textMuted};
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          line-height: 18px;
          white-space: nowrap;
        }
        .booking-entry-back:hover,
        .booking-entry-back:focus-visible {
          border-color: ${palette.accentBorder};
          color: ${palette.text};
          outline: none;
        }
        .booking-entry-breadcrumb {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: ${palette.textMuted};
          font-size: 12px;
          line-height: 18px;
        }
        .booking-entry-body { padding: 24px; }
        .booking-entry-branch { display: flex; align-items: center; gap: 12px; min-width: 0; }
        .booking-entry-logo {
          width: 42px; height: 42px; flex: 0 0 42px; overflow: hidden;
          border: ${palette.border}; border-radius: 10px; background: ${palette.surfaceAlt};
          color: ${palette.accent}; display: inline-flex; align-items: center;
          justify-content: center; font-size: 20px;
        }
        .booking-entry-logo img { width: 100%; height: 100%; object-fit: cover; }
        .booking-entry-branch__copy { min-width: 0; }
        .booking-entry-branch__copy h2 {
          margin: 0; color: ${palette.text}; font-size: 18px; line-height: 24px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .booking-entry-branch__copy p {
          margin: 2px 0 0; color: ${palette.textMuted}; font-size: 12px; line-height: 18px;
        }
        .booking-entry-intro h3 { margin: 0; color: ${palette.text}; font-size: 16px; line-height: 22px; }
        .booking-entry-intro p { margin: 3px 0 0; color: ${palette.textMuted}; font-size: 12px; line-height: 18px; }
        .booking-entry-options { display: grid; gap: 14px; }
        .booking-entry-option {
          min-height: 80px; padding: 10px 14px; border: ${palette.border}; border-radius: 12px;
          background: ${palette.surfaceAlt}; color: ${palette.text}; display: flex;
          align-items: center; gap: 12px; cursor: pointer;
          transition: border-color .15s ease, background .15s ease;
        }
        .booking-entry-option:hover,
        .booking-entry-option:focus-visible { border-color: ${palette.accentBorder}; outline: none; }
        .booking-entry-option__icon {
          width: 46px; height: 46px; flex: 0 0 46px; border: 1px solid ${palette.accentBorder};
          border-radius: 10px; background: ${palette.accentSoft}; color: ${palette.accent};
          display: inline-flex; align-items: center; justify-content: center; font-size: 18px;
        }
        .booking-entry-option__copy { flex: 1; min-width: 0; }
        .booking-entry-option__title { display: block; color: ${palette.text}; font-size: 14px; font-weight: 700; line-height: 20px; }
        .booking-entry-option__subtitle { display: block; color: ${palette.textMuted}; font-size: 12px; line-height: 18px; }
        @media (max-width: 575px) {
          .booking-entry-topbar { padding: 12px 14px; }
          .booking-entry-body { padding: 18px 14px; }
          .booking-entry-option { align-items: flex-start; }
          .booking-entry-option > svg { margin-top: 9px; }
        }
      `}</style>
    </StepCard>
  );
}

const WORKER_FALLBACK_AVATAR = "/assets/images/avatars/user.png";
const WORKER_FEEDBACK_FALLBACK_AVATAR = "/assets/images/avatars/specialist.png";

function WorkerAvatar({ palette, worker, fallbackSrc = WORKER_FALLBACK_AVATAR }) {
  const image = resolveStorageUrl(worker?.image_path);

  return (
    <span
      className="booking-worker-avatar"
      style={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        overflow: "hidden",
      }}
      aria-hidden="true"
    >
      <img
        src={image || fallbackSrc}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
        onError={(event) => {
          if (event.currentTarget.src.endsWith(fallbackSrc)) return;
          event.currentTarget.src = fallbackSrc;
        }}
      />
    </span>
  );
}

function getWorkerName(worker) {
  if (!worker) return "";
  return [worker.name, worker.surname].filter(Boolean).join(" ").trim();
}

function getWorkerProfession(worker) {
  if (Array.isArray(worker?.professions) && worker.professions.length > 0) {
    return worker.professions.map((item) => item.name).filter(Boolean).join(", ");
  }
  return "";
}

function getLinkedIndustryName({ industry, organization, branch, service, worker, lang }) {
  const candidates = [
    industry,
    organization?.industry,
    organization?.organization_type,
    organization?.organizationType,
    organization?.type,
    branch?.industry,
    branch?.organization_type,
    branch?.organizationType,
    branch?.type,
    service?.industry,
    service?.organization_type,
    service?.organizationType,
    service?.type,
    worker?.industry,
    worker?.organization_type,
    worker?.organizationType,
    worker?.type,
  ];

  for (const candidate of candidates) {
    const name =
      typeof candidate === "string"
        ? candidate
        : pickTranslation(candidate, lang) ||
        candidate?.title ||
        candidate?.label;
    if (name) return name;
  }

  return "";
}

function getWorkerServicesText(worker, t) {
  const count = Array.isArray(worker?.categories) ? worker.categories.length : 0;
  if (!count) return t("booking.specialists.noServices");
  return t("booking.specialists.servicesCount", { count });
}

function getWorkerRatingCount(worker) {
  const candidates = [
    worker?.reviews_count,
    worker?.reviewsCount,
    worker?.reviews?.count,
  ];

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value > 0) return value;
  }

  return 0;
}

function getWorkerRatingValue(worker) {
  const candidates = [
    worker?.rating_count,
    worker?.ratingCount,
    worker?.rating,
    worker?.ratings_count,
    worker?.ratingsCount,
    worker?.ratings?.value,
    worker?.ratings?.count,
  ];

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value > 0) return Math.min(5, Math.max(0, value));
  }

  return 0;
}

function getWorkerReviews(worker) {
  const candidates = [
    worker?.comments,
    worker?.reviews,
    worker?.feedbacks,
    worker?.ratings?.comments,
  ];

  return candidates.find((value) => Array.isArray(value)) || [];
}

function getWorkerReviewText(review) {
  return (
    review?.comment ||
    review?.feedback ||
    review?.text ||
    review?.message ||
    review?.description ||
    ""
  );
}

function getWorkerReviewDate(review) {
  const raw =
    review?.date ||
    review?.created_at ||
    review?.createdAt ||
    review?.updated_at ||
    review?.updatedAt ||
    "";
  if (!raw) return "";
  const value = String(raw);
  return value.includes("T") ? value.slice(0, 10) : value.split(" ")[0];
}

function getWorkerReviewAuthor(review, fallback) {
  return (
    review?.author ||
    review?.client_name ||
    review?.clientName ||
    review?.user?.name ||
    fallback
  );
}

function RatingStars({ value, onChange, disabled }) {
  return (
    <span className="booking-rating-stars d-inline-flex align-items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => onChange(star)}
          aria-label={`${star}`}
          style={{
            border: "none",
            background: "transparent",
            padding: 2,
            cursor: disabled ? "default" : "pointer",
            color: star <= value ? "#fbbf24" : "#cbd5e1",
            fontSize: "1.9rem",
            lineHeight: 1,
          }}
        >
          <i className="las la-star" aria-hidden="true" />
        </button>
      ))}
    </span>
  );
}

function ReadonlyStars({ value, size = "1rem" }) {
  const normalized = Math.min(5, Math.max(0, Number(value) || 0));
  return (
    <span className="booking-readonly-stars d-inline-flex align-items-center gap-1" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className="las la-star"
          style={{
            color: star <= normalized ? "#ffae00" : "#ffffff",
            fontSize: size,
            lineHeight: 1,
          }}
        />
      ))}
    </span>
  );
}

function WorkerRatingPanel({
  palette,
  lang,
  worker,
  orderTicketId,
  isAuthenticated,
  submitting,
  error,
  success,
  onSubmit,
  onLoginRequired,
}) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [remoteReviews, setRemoteReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const workerName = getWorkerName(worker);
  const profession = getWorkerProfession(worker);
  const inlineReviews = getWorkerReviews(worker);
  const reviews = remoteReviews.length > 0 ? remoteReviews : inlineReviews;
  const canSubmit = Boolean(rating && !submitting && (isAuthenticated ? orderTicketId : true));

  useEffect(() => {
    let cancelled = false;
    if (!worker?.id) {
      setRemoteReviews([]);
      setReviewsError(null);
      setReviewsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setReviewsLoading(true);
    setReviewsError(null);
    fetchRatingsByWorker(lang, worker.id, { page: 1, perPage: 25 })
      .then((payload) => {
        if (cancelled) return;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.ratings)
            ? payload.ratings
            : Array.isArray(payload?.data)
              ? payload.data
              : [];
        setRemoteReviews(list);
      })
      .catch((err) => {
        if (cancelled) return;
        setRemoteReviews([]);
        setReviewsError(err);
      })
      .finally(() => {
        if (!cancelled) setReviewsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lang, worker?.id]);

  const handleSubmit = () => {
    if (!isAuthenticated) {
      onLoginRequired?.();
      return;
    }
    onSubmit({ rating, comment });
  };

  return (
    <div
      className="booking-worker-feedback-panel"
      style={{
        display: "grid",
        gap: 12,
        fontFamily: BOOKING_FONT_FAMILY,
      }}
    >
      <div className="booking-worker-feedback-compose">
        <div className="booking-worker-feedback-summary">
          <WorkerAvatar
            palette={palette}
            worker={worker}
            fallbackSrc={WORKER_FEEDBACK_FALLBACK_AVATAR}
          />
          <div className="booking-worker-feedback-name">
            {workerName || t("booking.specialists.title")}
          </div>
          {profession ? (
            <div className="booking-worker-feedback-profession">{profession}</div>
          ) : null}
          <ReadonlyStars value={getWorkerRatingValue(worker)} size="1rem" />
        </div>

        <div className="booking-worker-feedback-form">
          <div className="booking-worker-feedback-title">
            {t("booking.feedback.title")}
          </div>
          <RatingStars value={rating} onChange={setRating} disabled={submitting} />
          <label className="booking-worker-feedback-label">
            {t("booking.feedback.commentLabel")}
          </label>
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            disabled={submitting}
            placeholder={t("booking.feedback.commentPlaceholder")}
            rows={3}
            className="booking-worker-feedback-textarea"
          />
          {isAuthenticated && !orderTicketId ? (
            <div className="booking-worker-feedback-note">
              {t("booking.feedback.afterBooking")}
            </div>
          ) : null}
          {error ? (
            <div className="booking-worker-feedback-error">
              {error.message || t("booking.feedback.error")}
            </div>
          ) : null}
          {success ? (
            <div className="booking-worker-feedback-success">
              {t("booking.feedback.success")}
            </div>
          ) : null}
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="booking-worker-feedback-submit pill fw-700"
          >
            {submitting ? t("booking.feedback.saving") : t("booking.feedback.submit")}
          </button>
        </div>
      </div>

      <div className="booking-worker-feedback-reviews">
        {reviewsLoading ? (
          <div className="booking-worker-feedback-empty">
            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
            {t("booking.common.loading")}
          </div>
        ) : reviewsError && reviews.length === 0 ? (
          <div className="booking-worker-feedback-empty">
            {t("booking.errors.loadServices")}
          </div>
        ) : reviews.length === 0 ? (
          <div className="booking-worker-feedback-empty">
            {t("booking.common.noReviews")}
          </div>
        ) : (
          reviews.map((review, index) => {
            const author = getWorkerReviewAuthor(review, workerName || "K");
            const text = getWorkerReviewText(review);
            return (
              <div key={review?.id || index} className="booking-worker-feedback-review">
                <span className="booking-worker-feedback-avatar" aria-hidden="true">
                  {String(author || "K").charAt(0).toUpperCase()}
                </span>
                <div className="booking-worker-feedback-review-copy">
                  <div className="booking-worker-feedback-author">
                    <strong>{author}</strong>
                    <i className="las la-info-circle" aria-hidden="true" />
                  </div>
                  <div className="booking-worker-feedback-meta">
                    <ReadonlyStars value={getWorkerRatingValue(review)} size="1rem" />
                    {getWorkerReviewDate(review) ? (
                      <span>{getWorkerReviewDate(review)}</span>
                    ) : null}
                  </div>
                  {text ? <p>{text}</p> : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      <style>{`
        .booking-worker-feedback-compose {
          display: grid;
          grid-template-columns: minmax(220px, 0.9fr) minmax(280px, 1fr);
          gap: clamp(16px, 4vw, 32px);
          padding: 0;
          align-items: start;
          min-width: 0;
        }

        .booking-worker-feedback-summary {
          background: ${palette.specialistBg || "transparent"};
          border: ${palette.border};
          border-radius: 14px;
          min-height: 216px;
          padding: 20px 18px;
          display: grid;
          justify-items: center;
          gap: 12px;
          text-align: center;
          color: ${palette.text};
          min-width: 0;
        }

        .booking-worker-feedback-summary .booking-worker-avatar {
          width: 154px !important;
          height: 154px !important;
          border-radius: 22px !important;
        }

        .booking-worker-feedback-name,
        .booking-worker-feedback-title {
          color: ${palette.text};
          font-size: 16.6px;
          font-weight: 700;
          line-height: 20.8px;
          overflow-wrap: anywhere;
        }

        .booking-worker-feedback-profession,
        .booking-worker-feedback-note,
        .booking-worker-feedback-review p {
          color: ${palette.textMuted};
          font-size: 13px;
          line-height: 19px;
          margin: 0;
        }

        .booking-worker-feedback-form {
          display: grid;
          gap: 12px;
          justify-items: start;
          min-width: 0;
          width: 100%;
        }

        .booking-rating-stars {
          max-width: 100%;
          flex-wrap: wrap;
        }

        .booking-worker-feedback-label {
          color: ${palette.text};
          font-size: 14.4px;
          line-height: 21.6px;
          font-weight: 600;
        }

        .booking-worker-feedback-textarea {
          width: 100%;
          min-height: 92px;
          border: ${palette.border};
          border-radius: 12px;
          background: ${palette.surfaceAlt};
          color: ${palette.text};
          font-family: ${BOOKING_FONT_FAMILY};
          font-size: 15.2px;
          line-height: 22.8px;
          outline: none;
          padding: 10px 15px;
          resize: vertical;
          overflow-wrap: anywhere;
        }

        .booking-worker-feedback-textarea::placeholder {
          color: #757575;
          opacity: 1;
        }

        .booking-worker-feedback-submit {
          width: 200px;
          min-height: 44px;
          border: none;
          background: ${palette.accent};
          color: #061415;
          padding: 10px 18px;
          opacity: ${canSubmit ? 1 : 0.55};
          cursor: ${canSubmit ? "pointer" : "not-allowed"};
          border-radius: 10px  !important;
        }

        .booking-worker-feedback-error {
          color: ${palette.danger || "#ef4444"};
          font-size: 13px;
        }

        .booking-worker-feedback-success {
          color: ${palette.success};
          font-size: 13px;
          font-weight: 700;
        }

        .booking-worker-feedback-reviews {
          display: grid;
          gap: 16px;
          min-width: 0;
        }

        .booking-worker-feedback-review,
        .booking-worker-feedback-empty {
          border: ${palette.border};
          border-radius: 14px;
          background: ${palette.specialistBg || palette.surface};
          color: ${palette.text};
          display: flex;
          gap: 12px;
          padding: 17px 19px;
          min-width: 0;
        }

        .booking-worker-feedback-empty {
          color: ${palette.textMuted};
          justify-content: center;
        }

        .booking-worker-feedback-avatar {
          width: 46px;
          height: 46px;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.2);
          color: ${palette.text};
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 46px;
          font-weight: 700;
        }

        .booking-worker-feedback-review-copy {
          display: grid;
          gap: 4px;
          min-width: 0;
          flex: 1;
        }

        .booking-worker-feedback-author {
          display: flex;
          align-items: center;
          gap: 4px;
          min-width: 0;
        }

        .booking-worker-feedback-author strong {
          color: ${palette.text};
          font-size: 16.6px;
          line-height: 20.8px;
          overflow-wrap: anywhere;
        }

        .booking-worker-feedback-author i {
          color: ${palette.textMuted};
          font-size: 18px;
          flex: 0 0 auto;
        }

        .booking-worker-feedback-meta {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          color: ${palette.textMuted};
          font-size: 14.4px;
          line-height: 20.88px;
        }

        @media (max-width: 991px) {
          .booking-worker-feedback-compose {
            grid-template-columns: 1fr;
          }

          .booking-worker-feedback-summary {
            min-height: 0;
            grid-template-columns: 64px minmax(0, 1fr);
            justify-items: start;
            align-items: center;
            text-align: left;
            gap: 4px 12px;
            padding: 14px;
          }

          .booking-worker-feedback-summary .booking-worker-avatar {
            grid-row: span 3;
            width: 64px !important;
            height: 64px !important;
            border-radius: 16px !important;
          }

          .booking-worker-feedback-summary .booking-readonly-stars {
            align-self: start;
          }
        }

        @media (max-width: 575px) {
          .booking-worker-feedback-compose {
            gap: 14px;
          }

          .booking-worker-feedback-summary {
            padding: 12px;
          }

          .booking-worker-feedback-textarea,
          .booking-worker-feedback-submit {
            width: 100%;
          }

          .booking-worker-feedback-review,
          .booking-worker-feedback-empty {
            padding: 14px 12px;
          }

          .booking-worker-feedback-review {
            gap: 10px;
          }

          .booking-worker-feedback-avatar {
            width: 38px;
            height: 38px;
            border-radius: 12px;
            flex-basis: 38px;
          }

          .booking-worker-feedback-author {
            align-items: flex-start;
          }
        }
      `}</style>
    </div>
  );
}

function WorkerOption({
  palette,
  worker,
  title,
  subtitle,
  selected,
  onClick,
  onFeedback,
  feedbackLabel,
}) {
  const { t } = useTranslation();
  const ratingCount = getWorkerRatingCount(worker);
  const ratingValue = getWorkerRatingValue(worker);
  const selectedBorder = `1px solid ${palette.accentBorder}`;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className="booking-worker-option text-start w-100"
      style={{
        background: palette.specialistBg || palette.surface,
        border: selected ? selectedBorder : palette.border,
        borderRadius: 14,
        padding: "17px 19px",
        color: palette.text,
        display: "grid",
        gap: 12,
        cursor: "pointer",
        minHeight: 132,
        transition: "background 0.15s ease, border-color 0.15s ease",
        fontFamily: BOOKING_FONT_FAMILY,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.border = selectedBorder;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.border = selected ? selectedBorder : palette.border;
      }}
    >
      <div className="booking-worker-top-row">
        <WorkerAvatar
          palette={palette}
          worker={worker}
        />
        <div className="booking-worker-main" style={{ flex: 1, minWidth: 0 }}>
          <div className="booking-worker-head">
            <span
              className="booking-worker-title"
              style={{
                color: palette.text,
                fontSize: 16.6,
                fontWeight: 700,
                lineHeight: "20.8px",
                fontFamily: BOOKING_FONT_FAMILY,
              }}
            >
              {title}
            </span>
          </div>
          <div
            className="booking-worker-rating-line"
          >
            <span className="booking-worker-stars" aria-hidden="true">
              {[1, 2, 3, 4, 5].map((star) => (
                <i
                  key={star}
                  className={`las la-star${star <= ratingValue ? " is-filled" : ""}`}
                />
              ))}
            </span>
            <span className="booking-worker-reviews">
              {t("booking.specialists.reviewsCount", { count: ratingCount })}
            </span>
            <button
              type="button"
              className="booking-worker-rate-button"
              onClick={(event) => {
                event.stopPropagation();
                onFeedback?.(worker);
              }}
            >
              {feedbackLabel}
            </button>
          </div>
          {subtitle ? (
            <span className="booking-worker-subtitle">{subtitle}</span>
          ) : null}
        </div>
        <span className={`booking-worker-radio${selected ? " is-selected" : ""}`} aria-hidden="true" />
      </div>
    </div>
  );
}

export function SpecialistSelectionStep({
  palette,
  lang,
  industry,
  organization,
  branch,
  service,
  workers,
  selectedWorker,
  orderTicketId,
  isAuthenticated,
  loading,
  error,
  continueLabel,
  continueDisabled,
  onSelect,
  onBack,
  onRetry,
  onContinueServices,
  onSubmitRating,
  onLoginRequired,
}) {
  const { t } = useTranslation();
  const selectedKey = selectedWorker?.id ?? null;
  const [feedbackWorker, setFeedbackWorker] = useState(null);
  const [ratingState, setRatingState] = useState("idle");
  const [ratingError, setRatingError] = useState(null);




  const closeFeedbackPanel = () => {
    setFeedbackWorker(null);
    setRatingState("idle");
    setRatingError(null);
  };

  const handleSubmitRating = async ({ rating, comment }) => {
    if (!feedbackWorker || !onSubmitRating) return;
    setRatingState("saving");
    setRatingError(null);
    try {
      await onSubmitRating({ worker: feedbackWorker, rating, comment });
      setRatingState("success");
    } catch (err) {
      setRatingError(err);
      setRatingState("error");
    }
  };
  const feedbackPanel = feedbackWorker ? (
    <WorkerRatingPanel
      palette={palette}
      lang={lang}
      worker={feedbackWorker}
      orderTicketId={orderTicketId}
      isAuthenticated={isAuthenticated}
      submitting={ratingState === "saving"}
      success={ratingState === "success"}
      error={ratingError}
      onSubmit={handleSubmitRating}
      onLoginRequired={onLoginRequired}
    />
  ) : null;
  const organizationName = pickTranslation(organization, lang);
  const industryName = getLinkedIndustryName({
    industry,
    organization,
    branch,
    service,
    worker: feedbackWorker || selectedWorker,
    lang,
  });
  const actionTextColor = palette.stepActiveText || "#fff";
  return (
    <StepCard
      palette={palette}
      style={{
        background: palette.surface,
        border: palette.border,
        borderRadius: 20,
        boxShadow: palette.shadow,
        display: "flex",
        flexDirection: "column",
        maxHeight: "min(82dvh, 820px)",
        overflow: "hidden",
      }}
    >
      <div className="booking-specialist-topbar">
        <button
          type="button"
          onClick={feedbackWorker ? closeFeedbackPanel : onBack}
          className="booking-specialist-back"
        >
          <i className="las la-arrow-left" aria-hidden="true" />
          {t("booking.common.back")}
        </button>
        {industryName || organizationName ? (
          <span className="booking-specialist-kind">
            {industryName ? `${industryName} / ` : ""}
            {organizationName}
          </span>
        ) : null}
      </div>
      <div
        className="booking-specialist-step-body"
        style={{
          padding: "24px",
          display: "grid",
          gap: 12,
          gridTemplateRows: feedbackWorker
            ? "minmax(0, 1fr)"
            : "auto minmax(0, 1fr)",
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {!feedbackWorker ? (
          <div className="booking-specialist-intro">
            <h3>{t("booking.specialists.title")}</h3>
            <p>{t("booking.specialists.subtitle")}</p>
          </div>
        ) : null}

        {loading ? (
          <SkeletonRow palette={palette} count={5} />
        ) : error ? (
          <EmptyState
            palette={palette}
            icon="las la-exclamation-circle"
            title={t("booking.specialists.error")}
            action={<RetryButton palette={palette} onClick={onRetry} />}
          />
        ) : (
          <div
            className="booking-specialist-scroll"
            style={{
              display: "grid",
              gap: 10,
              minHeight: 0,
              overflowY: "auto",
              paddingRight: 4,
            }}
          >
            {feedbackWorker ? feedbackPanel : workers.length === 0 ? (
              <EmptyState
                palette={palette}
                icon="las la-user-clock"
                title={t("booking.specialists.empty")}
              />
            ) : (
              workers.map((worker) => {
                const profession = getWorkerProfession(worker);
                const serviceText = getWorkerServicesText(worker, t);
                const workerSubtitle = [profession, serviceText].filter(Boolean).join(" • ");
                const isSelected = selectedKey === worker.id;
                return (
                  <WorkerOption
                    key={worker.id}
                    palette={palette}
                    worker={worker}
                    selected={isSelected}
                    title={getWorkerName(worker)}
                    subtitle={workerSubtitle}
                    onClick={() => onSelect(worker)}
                    onFeedback={setFeedbackWorker}
                    feedbackLabel={t("booking.feedback.button")}
                  />
                );
              })
            )}
          </div>
        )}
      </div>
      {!feedbackWorker ? (
        <StepFooter palette={palette} sticky>
          <button
            type="button"
            onClick={onContinueServices}
            disabled={continueDisabled}
            className="booking-specialist-continue pill fw-700"
            style={{
              background: "#00D3A1",
              color: "#033529",
              border: "none",
              padding: "10px 24px",
              borderRadius: "10px !important",
              opacity: continueDisabled ? 0.5 : 1,
              cursor: continueDisabled ? "not-allowed" : "pointer",
            }}
          >
            {continueLabel || t("booking.entryChoice.continueToServices")}
            <i className="las la-arrow-right ms-2" aria-hidden="true" />
          </button>
        </StepFooter>
      ) : null}
      <style>
        {`
          .booking-specialist-topbar {
            min-height: 60px;
            border-bottom: ${palette.border};
            padding: 15px 24px;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 12px;
            font-family: ${BOOKING_FONT_FAMILY};
          }

          .booking-specialist-back {
            min-height: 34px;
            border: ${palette.border};
            border-radius: 999px;
            background: transparent;
            color: ${palette.textMuted};
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 7px 17px;
            font-size: 16px;
            line-height: 20px;
            white-space: nowrap;
            flex-shrink: 0;
            transition: border-color 0.15s ease, color 0.15s ease;
          }

          .booking-specialist-back:hover,
          .booking-specialist-back:focus-visible {
            border-color: ${palette.accentBorder};
            color: ${palette.accent};
            outline: none;
          }

          .booking-specialist-kind {
            color: ${palette.textMuted};
            display: inline-flex;
            align-items: center;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 12px;
            line-height: 18px;
            white-space: nowrap;
          }

          .booking-specialist-branch {
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 0;
            margin-bottom: 8px;
          }

          .booking-specialist-logo {
            width: 57px;
            height: 58px;
            border-radius: 16px;
            border: ${palette.border};
            background: ${palette.surfaceAlt};
            box-shadow: ${palette.shadowSoft};
            color: ${palette.accent};
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
            font-size: 26px;
            overflow: hidden;
          }

          .booking-specialist-logo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
          }

          .booking-specialist-branch-copy {
            min-width: 0;
          }

          .booking-specialist-branch-copy h2,
          .booking-specialist-intro h3 {
            color: ${palette.text};
            font-family: ${BOOKING_FONT_FAMILY};
            font-size: 21.6px;
            font-weight: 700;
            line-height: 25.92px;
            margin: 0;
            overflow-wrap: anywhere;
          }

          .booking-specialist-branch-copy p,
          .booking-specialist-intro p {
            color: ${palette.textMuted};
            font-family: ${BOOKING_FONT_FAMILY};
            font-size: 15px;
            line-height: 24px;
            margin: 3px 0 0;
            overflow-wrap: anywhere;
          }

          .booking-specialist-intro {
            display: grid;
            gap: 3px;
            margin-bottom: 2px;
          }

          .booking-worker-option .booking-worker-avatar {
            width: 46px !important;
            height: 46px !important;
            border-radius: 14px !important;
            border: 1px solid ${palette.accentBorder} !important;
            background-color: ${palette.surfaceAlt} !important;
          }

          .booking-worker-top-row {
            display: grid;
            grid-template-columns: 46px minmax(0, 1fr) 28px;
            align-items: center;
            gap: 12px;
            min-width: 0;
          }

          .booking-worker-head {
            display: flex;
            align-items: flex-start;
            gap: 6px;
            min-width: 0;
          }

          .booking-worker-title {
            display: block;
            flex: 0 1 auto;
            min-width: 0;
            overflow-wrap: anywhere;
          }

          .booking-worker-info {
            width: 20px;
            height: 20px;
            border: 0;
            background: transparent;
            color: ${palette.textSubtle};
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            flex: 0 0 auto;
            font-size: 16px;
            line-height: 1;
          }

          .booking-worker-rating-line {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
            margin-top: 3px;
            min-width: 0;
          }

          .booking-worker-rate-button {
            border: ${palette.border};
            border-radius: 5px;
            background: ${palette.ratingButtonBg};
            color: ${palette.text};
            padding: 3px 8px;
            font-size: 11px;
            line-height: 16px;
            white-space: nowrap;
          }

          .booking-worker-rate-button:hover,
          .booking-worker-rate-button:focus-visible {
            border-color: ${palette.accentBorder};
            color: ${palette.accent};
            outline: none;
          }

          .booking-worker-stars {
            display: inline-flex;
            gap: 1px;
            font-size: 18px;
            line-height: 20px;
            white-space: nowrap;
          }

          .booking-worker-stars .la-star {
            color: ${palette.emptyStar};
          }

          .booking-worker-stars .la-star.is-filled {
            color: #ffae00;
          }

          .booking-worker-reviews {
            color: ${palette.textMuted};
            font-size: 14.4px;
            line-height: 20.88px;
            white-space: nowrap;
          }

          .booking-worker-subtitle {
            color: ${palette.textMuted};
            display: block;
            font-size: 14.4px;
            line-height: 20.88px;
            margin-top: 1px;
            overflow-wrap: anywhere;
          }

          .booking-worker-times-row {
            display: grid;
            grid-template-columns: auto minmax(0, 1fr);
            align-items: center;
            gap: 12px;
            margin-top: 0;
            min-width: 0;
          }

          .booking-worker-time-label {
            color: ${palette.textMuted};
            font-size: 14.4px;
            line-height: 20.88px;
            white-space: nowrap;
          }

          .booking-worker-time-label strong {
            color: ${palette.textMuted};
            font-weight: 700;
          }

          .booking-worker-times {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
            min-width: 0;
            overflow: visible;
          }

          .booking-worker-times::-webkit-scrollbar {
            display: none;
          }

          .booking-worker-time {
            border: ${palette.borderStrong || palette.border};
            appearance: none;
            min-height: 28px;
            min-width: 54px;
            border-radius: 5px;
            background: ${palette.surfaceAlt};
            color: ${palette.textMuted};
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 3px 8px;
            font-size: 15px;
            line-height: 20.88px;
            white-space: nowrap;
            transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
          }

          .booking-worker-time:hover,
          .booking-worker-time:focus-visible {
            border-color: ${palette.accentBorder};
            color: ${palette.accent};
            outline: none;
          }

          .booking-worker-time.is-featured {
            border-color: ${palette.accentBorder};
            background: ${palette.accent};
            color: ${actionTextColor};
          }

          .booking-worker-radio {
            width: 28px;
            height: 28px;
            border: 1.5px solid ${palette.accentBorder};
            border-radius: 999px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
            margin-left: auto;
          }

          .booking-worker-radio.is-selected::after {
            content: "";
            width: 16px;
            height: 16px;
            border-radius: 999px;
            background: ${palette.accent};
          }

          .booking-step-footer:has(.booking-specialist-continue) {
            justify-content: center !important;
            background: ${palette.surface} !important;
            border-top: ${palette.border} !important;
            padding: 12px 24px 15px !important;
          }

          .booking-specialist-continue {
            min-width: min(369px, 100%);
            min-height: 34px;
            border-radius: 10px !important;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            font-family: ${BOOKING_FONT_FAMILY};
            font-size: 16px;
            line-height: 24px;
          }

          .booking-specialist-scroll {
            scrollbar-width: thin;
            scrollbar-color: ${palette.accent} transparent;
          }

          .booking-specialist-scroll::-webkit-scrollbar {
            width: 7px;
          }

          .booking-specialist-scroll::-webkit-scrollbar-thumb {
            background: ${palette.accent};
            border-radius: 999px;
          }

          .booking-specialist-scroll::-webkit-scrollbar-track {
            background: transparent;
          }

          @media (max-width: 991px) {
            .booking-specialist-step-body {
              padding: 20px 16px 14px !important;
            }

            .booking-specialist-scroll {
              display: flex !important;
              flex-direction: column !important;
              gap: 10px !important;
              padding-right: 4px !important;
              overscroll-behavior: contain;
              -webkit-overflow-scrolling: touch;
            }

            .booking-worker-option {
              display: grid !important;
              gap: 12px !important;
              min-height: 0 !important;
              height: auto !important;
              padding: 14px !important;
              flex: 0 0 auto !important;
            }

            .booking-worker-top-row {
              grid-template-columns: 44px minmax(0, 1fr) 28px !important;
              align-items: center !important;
              gap: 10px !important;
            }

            .booking-worker-option .booking-worker-avatar {
              width: 44px !important;
              height: 44px !important;
            }

            .booking-worker-title {
              font-size: 15.4px !important;
              line-height: 19.5px !important;
            }

            .booking-worker-info {
              width: 18px;
              height: 18px;
              font-size: 15px;
            }

            .booking-worker-rating-line {
              gap: 5px 8px !important;
            }

            .booking-worker-stars {
              font-size: 16px !important;
              line-height: 18px !important;
            }

            .booking-worker-reviews {
              font-size: 12.8px !important;
              line-height: 17px !important;
              white-space: normal !important;
            }

            .booking-worker-subtitle {
              font-size: 13px !important;
              line-height: 18px !important;
            }

            .booking-worker-times-row {
              align-items: flex-start;
              grid-template-columns: 1fr !important;
              gap: 8px;
              padding-top: 10px;
              border-top: ${palette.border};
            }

            .booking-worker-time-label {
              white-space: normal;
            }

            .booking-worker-times {
              display: grid !important;
              grid-template-columns: repeat(auto-fit, minmax(64px, 1fr));
              gap: 6px !important;
              width: 100%;
            }

            .booking-worker-time {
              min-height: 32px;
              min-width: 0;
              width: 100%;
              border-radius: 8px;
              font-size: 14px;
            }

            .booking-worker-radio {
              width: 26px;
              height: 26px;
            }

            .booking-worker-radio.is-selected::after {
              width: 14px;
              height: 14px;
            }
          }

          @media (max-width: 575px) {
            .booking-specialist-topbar {
              padding: 12px 14px;
            }

            .booking-specialist-step-body {
              padding: 18px 14px 12px !important;
            }

            .booking-specialist-scroll {
              max-height: min(58dvh, 520px);
            }

            .booking-worker-option {
              padding: 14px 12px !important;
            }

            .booking-worker-top-row {
              grid-template-columns: 44px minmax(0, 1fr) 28px !important;
              align-items: center !important;
              gap: 10px !important;
            }

            .booking-worker-avatar {
              width: 44px !important;
              height: 44px !important;
              margin-top: 0 !important;
            }

            .booking-worker-main {
              display: block !important;
              min-width: 0 !important;
              padding-right: 0 !important;
            }

            .booking-worker-title {
              font-size: 15px !important;
              line-height: 19px !important;
            }

            .booking-worker-head {
              align-items: flex-start !important;
            }

            .booking-worker-rating-line {
              gap: 5px 8px !important;
            }

            .booking-worker-stars {
              font-size: 16px !important;
              line-height: 18px !important;
            }

            .booking-worker-reviews {
              font-size: 12.8px !important;
              line-height: 17px !important;
              white-space: normal !important;
            }

            .booking-worker-subtitle {
              font-size: 0.78rem !important;
              line-height: 1.3 !important;
              overflow-wrap: anywhere !important;
            }

            .booking-worker-times-row {
              align-items: flex-start;
              grid-template-columns: 1fr !important;
              gap: 8px;
              margin-top: 0;
            }

            .booking-worker-time-label {
              white-space: normal;
              font-size: 12.8px !important;
              line-height: 17px !important;
            }

            .booking-worker-times {
              display: grid !important;
              grid-template-columns: repeat(auto-fit, minmax(64px, 1fr));
              gap: 6px !important;
              width: 100%;
            }

            .booking-worker-time {
              font-size: 14px;
            }

            .booking-worker-radio {
              width: 28px;
              height: 28px;
            }

            .booking-specialist-continue {
              min-width: 100%;
            }
          }

          @media (min-width: 481px) {
            .booking-specialist-scroll {
              max-height: min(52vh, 560px);
              overscroll-behavior: contain;
            }
          }

        `}
      </style>
    </StepCard>
  );
}
